import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Landmark, Banknote, CreditCard, Plus, Trash2, Save,
  Loader2, Check, Info, Globe, Bell, Clock, Send, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const PAYMENT_TYPES = [
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Landmark },
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'other', label: 'Other', icon: Globe },
];

const emptyMethod = {
  type: 'bank_transfer',
  enabled: true,
  label: '',
  bank_name: '',
  account_holder: '',
  iban: '',
  swift_bic: '',
  sort_code: '',
  account_number: '',
  instructions: '',
};

const PaymentSettingsPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [shop, setShop] = useState(null);
  const [methods, setMethods] = useState([]);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderDays, setReminderDays] = useState([7, 14, 30]);
  const [maxReminders, setMaxReminders] = useState(3);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) return;
      const s = shopsRes.data[0];
      setShop(s);

      const settingsRes = await api.get(`/shops/${s.id}/payment-settings`);
      if (settingsRes.data.methods?.length > 0) {
        setMethods(settingsRes.data.methods);
      }

      const reminderRes = await api.get(`/shops/${s.id}/reminder-settings`);
      if (reminderRes.data) {
        setReminderEnabled(reminderRes.data.enabled || false);
        setReminderDays(reminderRes.data.days || [7, 14, 30]);
        setMaxReminders(reminderRes.data.max_reminders || 3);
      }
    } catch (e) {
      console.error('Failed to load payment settings:', e);
    } finally { setLoading(false); }
  };

  const addMethod = () => setMethods([...methods, { ...emptyMethod }]);

  const removeMethod = (i) => setMethods(methods.filter((_, idx) => idx !== i));

  const updateMethod = (i, field, val) => {
    const copy = [...methods];
    copy[i] = { ...copy[i], [field]: val };
    setMethods(copy);
  };

  const handleSave = async () => {
    if (!shop) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.put(`/shops/${shop.id}/payment-settings`, { methods });
      await api.put(`/shops/${shop.id}/reminder-settings`, {
        enabled: reminderEnabled,
        days: reminderDays,
        max_reminders: maxReminders,
        language: localStorage.getItem('vf_language') || 'es',
        portal_base_url: window.location.origin,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving settings');
    } finally { setSaving(false); }
  };

  const handleSendNow = async () => {
    if (!shop) return;
    setSendingReminders(true);
    setReminderResult(null);
    try {
      const res = await api.post(`/shops/${shop.id}/reminders/send-now`);
      setReminderResult(res.data);
    } catch (e) {
      setReminderResult({ error: e.response?.data?.detail || 'Error sending reminders' });
    } finally { setSendingReminders(false); }
  };

  const addReminderDay = () => {
    const last = reminderDays.length > 0 ? Math.max(...reminderDays) : 0;
    setReminderDays([...reminderDays, last + 7]);
  };

  const removeReminderDay = (i) => setReminderDays(reminderDays.filter((_, idx) => idx !== i));

  const updateReminderDay = (i, val) => {
    const num = parseInt(val);
    if (isNaN(num) || num < 1) return;
    const copy = [...reminderDays];
    copy[i] = num;
    setReminderDays(copy);
  };

  if (loading) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="space-y-6 max-w-3xl" data-testid="payment-settings-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('paysettings_title')}</h2>
            <p className="text-slate-500 text-sm">{t('paysettings_subtitle')}</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="vf-btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="save-payment-settings"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saved ? t('paysettings_saved') : t('paysettings_save')}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-blue-700 text-sm">{t('paysettings_info')}</p>
        </div>

        {/* Payment Methods */}
        {methods.length === 0 ? (
          <div className="bg-white border-2 border-slate-100 p-8 text-center">
            <Landmark className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 mb-4">{t('paysettings_empty')}</p>
            <button
              onClick={addMethod}
              className="vf-btn-primary inline-flex items-center gap-2"
              data-testid="add-first-payment-method"
            >
              <Plus className="w-4 h-4" /> {t('paysettings_add')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {methods.map((method, i) => {
              const TypeIcon = PAYMENT_TYPES.find(p => p.value === method.type)?.icon || CreditCard;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border-2 border-slate-100 p-5"
                  data-testid={`payment-method-${i}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <TypeIcon className="w-5 h-5 text-[#1e3a5f]" />
                      <select
                        value={method.type}
                        onChange={e => updateMethod(i, 'type', e.target.value)}
                        className="px-3 py-1.5 border border-slate-200 rounded-sm text-sm font-medium focus:border-[#1e3a5f] focus:outline-none"
                        data-testid={`method-type-${i}`}
                      >
                        {PAYMENT_TYPES.map(pt => (
                          <option key={pt.value} value={pt.value}>{pt.label}</option>
                        ))}
                      </select>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <button
                          onClick={() => updateMethod(i, 'enabled', !method.enabled)}
                          className={`w-10 h-5 rounded-full transition-colors ${method.enabled ? 'bg-[#22c55e]' : 'bg-slate-300'}`}
                          data-testid={`method-toggle-${i}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${method.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                        <span className="text-xs text-slate-500">{method.enabled ? 'Active' : 'Disabled'}</span>
                      </label>
                    </div>
                    <button
                      onClick={() => removeMethod(i)}
                      className="p-2 text-slate-400 hover:text-red-500"
                      data-testid={`remove-method-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">{t('paysettings_label')}</label>
                      <input
                        type="text"
                        value={method.label || ''}
                        onChange={e => updateMethod(i, 'label', e.target.value)}
                        placeholder="e.g. EU Bank Transfer"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                        data-testid={`method-label-${i}`}
                      />
                    </div>

                    {method.type === 'bank_transfer' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">{t('paysettings_bank_name')}</label>
                          <input
                            type="text"
                            value={method.bank_name || ''}
                            onChange={e => updateMethod(i, 'bank_name', e.target.value)}
                            placeholder="e.g. Santander, HSBC, Revolut"
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                            data-testid={`method-bank-${i}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">{t('paysettings_holder')}</label>
                          <input
                            type="text"
                            value={method.account_holder || ''}
                            onChange={e => updateMethod(i, 'account_holder', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                            data-testid={`method-holder-${i}`}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-medium text-slate-500 mb-1">IBAN</label>
                          <input
                            type="text"
                            value={method.iban || ''}
                            onChange={e => updateMethod(i, 'iban', e.target.value.toUpperCase())}
                            placeholder="ES91 2100 0418 4502 0005 1332"
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-[#1e3a5f] focus:outline-none"
                            data-testid={`method-iban-${i}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">SWIFT / BIC</label>
                          <input
                            type="text"
                            value={method.swift_bic || ''}
                            onChange={e => updateMethod(i, 'swift_bic', e.target.value.toUpperCase())}
                            placeholder="CAIXESBBXXX"
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-[#1e3a5f] focus:outline-none"
                            data-testid={`method-swift-${i}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">{t('paysettings_sort_code')} <span className="text-slate-400">(UK)</span></label>
                          <input
                            type="text"
                            value={method.sort_code || ''}
                            onChange={e => updateMethod(i, 'sort_code', e.target.value)}
                            placeholder="12-34-56"
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-[#1e3a5f] focus:outline-none"
                            data-testid={`method-sort-${i}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">{t('paysettings_acc_number')} <span className="text-slate-400">(UK)</span></label>
                          <input
                            type="text"
                            value={method.account_number || ''}
                            onChange={e => updateMethod(i, 'account_number', e.target.value)}
                            placeholder="12345678"
                            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-[#1e3a5f] focus:outline-none"
                            data-testid={`method-accnum-${i}`}
                          />
                        </div>
                      </>
                    )}

                    {(method.type === 'cash' || method.type === 'card' || method.type === 'other') && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-slate-500 mb-1">{t('paysettings_instructions')}</label>
                        <textarea
                          value={method.instructions || ''}
                          onChange={e => updateMethod(i, 'instructions', e.target.value)}
                          rows={2}
                          placeholder={
                            method.type === 'cash'
                              ? 'e.g. Visit our shop at Calle Mayor 12, Madrid'
                              : method.type === 'card'
                              ? 'e.g. Call us at +34 612 345 678 for card payment'
                              : 'e.g. PayPal: shop@example.com'
                          }
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none resize-none"
                          data-testid={`method-instructions-${i}`}
                        />
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}

            <button
              onClick={addMethod}
              className="w-full border-2 border-dashed border-slate-300 py-3 text-slate-500 hover:border-[#22c55e] hover:text-[#22c55e] transition-colors flex items-center justify-center gap-2"
              data-testid="add-payment-method"
            >
              <Plus className="w-4 h-4" /> {t('paysettings_add')}
            </button>
          </div>
        )}

        {/* Reminder Settings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border-2 border-slate-100 p-5 space-y-4"
          data-testid="reminder-settings-section"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-[#1e3a5f]">{t('reminder_title')}</h3>
            </div>
            <button
              onClick={() => setReminderEnabled(!reminderEnabled)}
              className={`w-12 h-6 rounded-full transition-colors ${reminderEnabled ? 'bg-[#22c55e]' : 'bg-slate-300'}`}
              data-testid="reminder-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${reminderEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <p className="text-sm text-slate-500">{t('reminder_desc')}</p>

          {reminderEnabled && (
            <div className="space-y-4 pt-2">
              {/* Reminder Days */}
              <div>
                <label className="block text-xs font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> {t('reminder_days_label')}
                </label>
                <div className="space-y-2">
                  {reminderDays.map((day, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-20">{t('reminder_after')}</span>
                      <input
                        type="number"
                        min={1}
                        value={day}
                        onChange={e => updateReminderDay(i, e.target.value)}
                        className="w-20 px-3 py-1.5 border border-slate-200 rounded-sm text-sm text-center focus:border-[#1e3a5f] focus:outline-none"
                        data-testid={`reminder-day-${i}`}
                      />
                      <span className="text-xs text-slate-500">{t('reminder_days_unit')}</span>
                      {reminderDays.length > 1 && (
                        <button onClick={() => removeReminderDay(i)} className="p-1 text-slate-400 hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addReminderDay}
                    className="text-sm text-[#22c55e] hover:underline flex items-center gap-1"
                    data-testid="add-reminder-day"
                  >
                    <Plus className="w-3.5 h-3.5" /> {t('reminder_add_day')}
                  </button>
                </div>
              </div>

              {/* Max Reminders */}
              <div>
                <label className="block text-xs font-semibold text-[#1e3a5f] mb-1">{t('reminder_max')}</label>
                <select
                  value={maxReminders}
                  onChange={e => setMaxReminders(parseInt(e.target.value))}
                  className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                  data-testid="max-reminders-select"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              {/* Manual Send */}
              <div className="border-t border-slate-100 pt-4">
                <button
                  onClick={handleSendNow}
                  disabled={sendingReminders}
                  className="bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="send-reminders-now"
                >
                  {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t('reminder_send_now')}
                </button>
                {reminderResult && (
                  <p className={`text-sm mt-2 ${reminderResult.error ? 'text-red-600' : 'text-green-600'}`} data-testid="reminder-result">
                    {reminderResult.error || `${reminderResult.sent_count} ${t('reminder_sent_count')}`}
                  </p>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </ShopLayout>
  );
};

export default PaymentSettingsPage;
